import {klona} from 'klona'

import {type FoodCartItem, type FoodCartV1} from './foodModel'

export type FoodCartItemWithoutQuantity = Omit<FoodCartItem, 'quantity'>

function findItemIndex(
  foodCartItems: FoodCartV1['foodCartItems'],
  item: FoodCartItemWithoutQuantity,
) {
  // Check if the item is exist the cart via compare productId, and all the selectedOptions with quantity
  return foodCartItems.findIndex(i => {
    return (
      i.productId === item.productId &&
      i.selectedOptions.every(option =>
        item.selectedOptions.some(
          option2 => option2.optionId === option.optionId && option2.quantity === option.quantity,
        ),
      )
    )
  })
}

function clearFoodCartIfStoreIdChanged(foodCart: FoodCartV1, item: FoodCartItemWithoutQuantity) {
  if (foodCart.foodCartItems.some(i => i.storeId !== item.storeId)) {
    foodCart.foodCartItems = []
  }
}

export function addToFoodCart({
  foodCart,
  item,
  quantity = 1,
}: {
  foodCart: FoodCartV1
  item: FoodCartItemWithoutQuantity
  quantity?: number
}) {
  const newFoodCart = klona(foodCart)

  clearFoodCartIfStoreIdChanged(newFoodCart, item)

  const existItemIndex = findItemIndex(foodCart.foodCartItems, item)

  if (existItemIndex !== -1) {
    invariant(newFoodCart.foodCartItems[existItemIndex])
    newFoodCart.foodCartItems[existItemIndex].quantity += quantity
  } else {
    newFoodCart.foodCartItems.unshift({
      ...item,
      quantity,
    })
  }

  return newFoodCart
}

export function removeFromFoodCart({
  foodCart,
  itemOrIndex,
  quantity,
}: {
  foodCart: FoodCartV1
  itemOrIndex: FoodCartItemWithoutQuantity | number
  quantity?: number // if quantity is 0, <0 or missing, remove the item entirely
}) {
  const newFoodCart = klona(foodCart)

  const existItemIndex =
    typeof itemOrIndex === 'number'
      ? itemOrIndex
      : findItemIndex(foodCart.foodCartItems, itemOrIndex)

  if (existItemIndex !== -1) {
    invariant(newFoodCart.foodCartItems[existItemIndex])

    if (quantity && quantity > 0 && newFoodCart.foodCartItems[existItemIndex].quantity > quantity) {
      newFoodCart.foodCartItems[existItemIndex].quantity -= quantity
    } else {
      newFoodCart.foodCartItems.splice(existItemIndex, 1)
    }
  }

  return newFoodCart
}

export function updateFoodCartProductQuantity({
  foodCart,
  itemOrIndex,
  quantity,
}: {
  foodCart: FoodCartV1
  itemOrIndex: FoodCartItemWithoutQuantity | number
  quantity: number
}) {
  const newFoodCart = klona(foodCart)

  const existItemIndex =
    typeof itemOrIndex === 'number'
      ? itemOrIndex
      : findItemIndex(foodCart.foodCartItems, itemOrIndex)

  if (existItemIndex !== -1) {
    if (quantity > 0) {
      if (newFoodCart.foodCartItems[existItemIndex]) {
        newFoodCart.foodCartItems[existItemIndex].quantity = quantity
      }
    } else {
      newFoodCart.foodCartItems.splice(existItemIndex, 1)
    }
  } else if (typeof itemOrIndex !== 'number') {
    newFoodCart.foodCartItems.unshift({
      ...itemOrIndex,
      quantity,
    })
  }

  return newFoodCart
}
